import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const CassandraCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const cassandraHost = annotations['cassandra.infrastructure/host'];
  const cassandraPort = annotations['cassandra.infrastructure/port'];
  const cassandraKeyspace = annotations['cassandra.infrastructure/keyspace'];
  const cassandraUser = annotations['cassandra.infrastructure/user'];
  const cassandraPassword = annotations['cassandra.infrastructure/password'];
  const cassandraVersion = annotations['cassandra.infrastructure/version'];
  const cdcEnabled = annotations['cassandra.infrastructure/cdc-enabled'];
  const sshCommand = annotations['cassandra.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded = annotations['cassandra.infrastructure/ssh-private-key'];

  if (templateType !== 'aws-cassandra-cdc-source' || !cassandraHost) return null;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'cassandra_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const host = cassandraHost;
  const port = cassandraPort || '9042';
  const keyspace = cassandraKeyspace || 'rdi_test';
  const user = cassandraUser || 'rdi_user';
  const password = cassandraPassword || 'RdiCassandra2024!';

  const rdiConfig = `connection:
  type: cassandra
  host: ${host}
  port: ${port}
  keyspace: ${keyspace}
  user: ${user}
  password: ${password}
advanced:
  source:
    connector.class: io.debezium.connector.cassandra.CassandraConnector
    cassandra.cdc.dir: /var/lib/cassandra/cdc_raw`;

  const cqlCommands = `# Connect to Cassandra
docker exec -it cassandra cqlsh -u ${user} -p ${password}

# List keyspaces
DESCRIBE KEYSPACES;

# Use keyspace
USE ${keyspace};

# List tables
DESCRIBE TABLES;

# Check CDC status
SELECT table_name, cdc FROM system_schema.tables WHERE keyspace_name = '${keyspace}';`;

  const cqlConnect = `docker exec -it cassandra cqlsh -u ${user} -p ${password}`;

  return (
    <InfoCard title="Cassandra CDC Source Information" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🔷 Cassandra Configuration">
          {cassandraVersion && (
            <InfraField
              label="Version:"
              value={<InfraTagValue>{`Cassandra ${cassandraVersion}`}</InfraTagValue>}
              mono={false}
            />
          )}
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={
                <InfraTagRow>
                  <InfraTag>
                    <RiCheckLine size={14} style={{ marginRight: 4 }} />
                    Enabled
                  </InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          <InfraField
            label="CDC Type:"
            value={<InfraTagValue>CommitLog-based</InfraTagValue>}
            mono={false}
          />
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          <CopyableValue label="Host:" value={host} />
          <InfraField label="Port (CQL):" value={port} />
          <InfraField label="Keyspace:" value={keyspace} />
          <InfraField label="User:" value={user} />
          {cassandraPassword && <CopyableValue label="Password:" value={cassandraPassword} />}
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🚀 RDI Configuration">
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfig}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfig} />
            </div>
          </div>
        </InfraSection>

        <InfraDivider />
        <InfraSection title="📝 CQL Shell Commands">
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{cqlCommands}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={cqlConnect} />
            </div>
          </div>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshCommand && (
                <>
                  <InfraField label="SSH Command:" value="" mono={false} />
                  <div style={{ position: 'relative' }}>
                    <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
                    <div style={{ position: 'absolute', top: 4, right: 4 }}>
                      <CopyButton text={sshCommand} />
                    </div>
                  </div>
                </>
              )}
              <InfraField label="SSH Private Key:" value="" mono={false} />
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                >
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>
              <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                💡 Save as cassandra_key.pem and run: chmod 400 cassandra_key.pem
              </Text>
            </InfraSection>
          </>
        )}
      </InfraStack>
    </InfoCard>
  );
};
